/* eslint-disable react/jsx-props-no-spreading, react/require-default-props */
import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'utils/classNamesTailwind';
import { TbIcon, type TbIconProps } from 'components/Figma';

const buttonVariants = cva(
  'inline-flex items-center justify-center border font-bold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ash-600 disabled:cursor-not-allowed disabled:text-ash-300 whitespace-nowrap',
  {
    variants: {
      iconButton: {
        false: '',
        true: '',
      },
      size: {
        lg: 'h-[45px] gap-2 px-3 py-3 text-[16px] rounded-md',
        md: 'h-[40px] gap-2 px-3 py-3 text-[14px] rounded-md',
        sm: 'h-8 gap-1.5 px-2 py-1.5 text-[13px] rounded',
      },
      variant: {
        primary: '',
        critical: '',
      },
      priority: {
        primary: 'text-snow-400 border-[1px] disabled:bg-snow-400 disabled:border-snow-400',
        secondary: 'bg-transparent active:bg-transparent disabled:border-snow-400',
        ternery: 'disabled:bg-snow-200',
        ghost: 'border-transparent bg-transparent active:bg-transparent active:border-transparent',
      },
    },
    compoundVariants: [
      {
        iconButton: true,
        size: 'lg',
        className: 'w-[45px]',
      },
      {
        iconButton: true,
        size: 'md',
        className: 'w-[40px] max-w-[40px]',
      },
      {
        iconButton: true,
        size: 'sm',
        className: 'w-32',
      },
      {
        variant: 'primary',
        priority: 'primary',
        className:
          'bg-ash-900 border-ash-900 ' +
          'hover:bg-ash-600 hover:border-ash-600 ' +
          'active:bg-ash-700 active:border-ash-700',
      },
      {
        variant: 'critical',
        priority: 'primary',
        className:
          'bg-coral-500 border-coral-500 ' +
          'hover:bg-coral-600 hover:border-coral-600 ' +
          'active:bg-coral-700 active:border-coral-700',
      },
      {
        variant: 'primary',
        priority: 'secondary',
        className: 'border-ash-900 text-ash-900 hover:bg-snow-200 bg-snow-50 active:bg-snow-50',
      },
      {
        variant: 'critical',
        priority: 'secondary',
        className:
          'text-coral-500 border-coral-500 bg-coral-50' +
          'hover:text-coral-600 hover:border-coral-600 hover:bg-coral-200 ' +
          'active:text-coral-700 active:border-coral-700 active:bg-coral-50',
      },
      {
        variant: 'primary',
        priority: 'ternery',
        className:
          'text-ash-900 bg-snow-200 border-snow-200  ' +
          'hover:text-snow-50 hover:bg-ash-900 hover:border-ash-900 ' +
          'active:bg-ash-700 active:border-ash-700',
      },
      {
        variant: 'critical',
        priority: 'ternery',
        className:
          'text-coral-600 bg-coral-50 border-coral-50 ' +
          'hover:text-snow-50 hover:bg-coral-500 hover:border-coral-500 ' +
          'active:bg-coral-700 active:border-coral-700',
      },
      {
        variant: 'primary',
        priority: 'ghost',
        className: 'text-ash-900 hover:bg-snow-200 hover:border-snow-200 ',
      },
      {
        variant: 'critical',
        priority: 'ghost',
        className:
          'text-coral-500 hover:text-coral-600 ' +
          'active:text-coral-700 ' +
          'hover:bg-coral-50  hover:border-coral-50 ',
      },
    ],
    defaultVariants: {
      size: 'md',
      variant: 'primary',
      priority: 'primary',
    },
  }
);

export interface ButtonProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>,
    Omit<VariantProps<typeof buttonVariants>, 'iconButton'> {
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  iconLeft?: TbIconProps['name'];
  iconRight?: TbIconProps['name'];
  icon?: TbIconProps['name'];
  iconSize?: TbIconProps['size'];
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    size = 'md',
    variant = 'primary',
    priority = undefined,
    iconLeft = undefined,
    iconRight = undefined,
    icon = undefined,
    iconSize = undefined,
    children = undefined,
    ...props
  },
  ref
) {
  const iconMeasurement = iconSize ? iconSize : icon ? (size === 'sm' ? 'md' : size === 'md' ? 'lg' : 'xl') : size;
  const isIconButton = Boolean(icon);
  const effectivePriority = priority ?? (icon ? 'ghost' : 'primary');

  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        buttonVariants({ size, variant, priority: effectivePriority, iconButton: isIconButton }),
        className
      )}
      {...props}
    >
      {icon ? (
        <TbIcon name={icon} size={iconMeasurement} />
      ) : (
        <>
          {iconLeft && <TbIcon name={iconLeft} size={iconMeasurement} />}
          {children && children}
          {iconRight && <TbIcon name={iconRight} size={iconMeasurement} />}
        </>
      )}
    </button>
  );
});

Button.displayName = 'Button';
